import { apiClient, type ApiRequestConfig } from '@/lib/api'
import { ApiResponse, AuthResponse, LoginForm, CustomerRegisterForm } from '@/types'

export type AuthSessionData = AuthResponse & {
  expires_in?: number
  idle_timeout_minutes?: number
  max_session_expires_at?: string
  pending_2fa?: boolean
  challenge_id?: string
  shop_selection_required?: boolean
  shops?: Array<{
    shop_id: number
    shop_name: string
    shop_code?: string | null
  }>
}

export const authService = {
  staffLogin: async (data: LoginForm): Promise<ApiResponse<AuthSessionData>> => {
    return apiClient.post('/auth/staff/login', data)
  },

  customerLogin: async (data: LoginForm): Promise<ApiResponse<AuthSessionData>> => {
    return apiClient.post('/auth/customer/login', data)
  },

  verifyTwoFactorLogin: async (data: {
    challenge_id: string
    token: string
  }): Promise<ApiResponse<AuthSessionData>> => {
    return apiClient.post('/auth/2fa/verify-login', data)
  },

  getTwoFactorStatus: async (): Promise<
    ApiResponse<{ enabled: boolean; required: boolean; user_type: string }>
  > => apiClient.get('/auth/2fa/status'),

  setupTwoFactor: async (): Promise<
    ApiResponse<{ otpauth_url: string; secret: string; qr_code_data_url: string }>
  > => apiClient.post('/auth/2fa/setup'),

  confirmTwoFactor: async (token: string): Promise<ApiResponse<{ recovery_codes: string[] }>> =>
    apiClient.post('/auth/2fa/confirm', { token }),

  disableTwoFactor: async (token: string): Promise<ApiResponse> =>
    apiClient.post('/auth/2fa/disable', { token }),

  customerRegister: async (
    data: CustomerRegisterForm & { locale?: string; redirect_path?: string },
  ): Promise<
    ApiResponse<
      | AuthResponse
      | {
          pending_email_verification: true
          email: string
          shop_id: number
          redirect_path?: string | null
        }
    >
  > => {
    return apiClient.post('/auth/customer/register', data)
  },

  verifyCustomerEmail: async (
    token: string,
  ): Promise<
    ApiResponse<
      AuthSessionData & {
        redirect_path?: string | null
        email_verified?: boolean
        already_verified?: boolean
      }
    >
  > => apiClient.post('/auth/customer/verify-email', { token }),

  resendCustomerVerification: async (data: {
    email: string
    shop_id: number
    locale?: string
    redirect_path?: string
  }): Promise<ApiResponse> => apiClient.post('/auth/customer/resend-verification', data),

  changePassword: async (data: {
    current_password: string
    new_password: string
    confirm_password: string
  }): Promise<ApiResponse> => {
    return apiClient.post('/auth/change-password', data)
  },

  logout: async (): Promise<ApiResponse> => {
    return apiClient.post('/auth/logout', undefined, { suppressErrorToast: true } satisfies ApiRequestConfig)
  },

  verifyToken: async (): Promise<ApiResponse<{ user: unknown }>> => {
    return apiClient.get('/auth/verify', { suppressErrorToast: true } satisfies ApiRequestConfig)
  },

  refreshToken: async (): Promise<ApiResponse<AuthSessionData>> => {
    return apiClient.post('/auth/refresh')
  },

  forgotPassword: async (
    email: string,
    user_type?: string,
    locale?: string,
    shop_id?: number
  ): Promise<
    ApiResponse<{
      shop_selection_required?: boolean
      shops?: Array<{
        shop_id: number
        shop_name: string
        shop_code?: string | null
      }>
    }>
  > => {
    return apiClient.post('/auth/forgot-password', {
      email,
      user_type: user_type || 'customer',
      locale,
      ...(shop_id != null ? { shop_id } : {}),
    })
  },

  resetPassword: async (data: {
    token: string
    password: string
    confirm_password: string
  }): Promise<ApiResponse> => {
    return apiClient.post('/auth/reset-password', data)
  },

  staffViewAsCustomer: async (customerId: number): Promise<ApiResponse<AuthResponse>> => {
    return apiClient.post(`/auth/staff/view-as-customer/${customerId}`)
  },

  /** Platform super_admin: put shop_id into the current session/JWT. */
  actAsShop: async (
    shopId: number,
  ): Promise<
    ApiResponse<
      AuthSessionData & {
        shop?: { shop_id: number; name: string; shop_code?: string }
      }
    >
  > => {
    return apiClient.post(`/auth/act-as-shop/${shopId}`)
  },

  /** Platform super_admin: clear acting shop context. */
  clearActingShop: async (): Promise<ApiResponse<AuthSessionData>> => {
    return apiClient.post('/auth/clear-acting-shop')
  },
}
